import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { DoneAll, MarkEmailUnread, Visibility } from '@mui/icons-material';
import {
  CustomButton,
  DialogBody,
  DialogContainer,
  DialogTitle,
  GeneralTableBox,
  HeadTableCell,
  LoadingSkeleton,
  NoDataSkeleton,
  PaginationTable,
  RowTableCell,
  TableTitleBox,
} from '../../common/components';
import {
  useMarkAllMyNotificationsRead,
  useMarkMyNotificationRead,
  useMarkMyNotificationUnread,
  useMyNotifications,
} from '../../hooks/useNotifications';

const formatDateTime = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('es-AR');
};

const readStatusChip = (notification) => (
  <Chip
    label={notification?.is_read ? 'Leída' : 'No leída'}
    color={notification?.is_read ? 'default' : 'primary'}
    size="small"
  />
);

const ProfileNotifications = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [detailNotification, setDetailNotification] = useState(null);

  const { data, isLoading, isFetching } = useMyNotifications({
    page,
    per_page: rowsPerPage,
  });
  const markReadMutation = useMarkMyNotificationRead();
  const markUnreadMutation = useMarkMyNotificationUnread();
  const markAllReadMutation = useMarkAllMyNotificationsRead();

  const notifications = useMemo(() => data?.results || [], [data?.results]);
  const actionLoading =
    markReadMutation.isPending || markUnreadMutation.isPending || markAllReadMutation.isPending;
  const notificationFromQuery = searchParams.get('notification');

  const notificationByUuid = useMemo(
    () => new Map(notifications.map((notification) => [notification.uuid, notification])),
    [notifications]
  );

  useEffect(() => {
    if (!notificationFromQuery || isLoading) return;
    const notification = notificationByUuid.get(notificationFromQuery);
    if (notification) {
      setDetailNotification(notification);
    }
  }, [isLoading, notificationByUuid, notificationFromQuery]);

  const handleOpenDetail = async (notification) => {
    if (!notification.is_read) {
      await markReadMutation.mutateAsync(notification.uuid);
    }
    setDetailNotification({ ...notification, is_read: true });
    setSearchParams({ notification: notification.uuid });
  };

  const handleCloseDetail = () => {
    setDetailNotification(null);
    setSearchParams({});
  };

  const handleToggleRead = (notification) => {
    if (notification.is_read) {
      markUnreadMutation.mutate(notification.uuid);
      return;
    }
    markReadMutation.mutate(notification.uuid);
  };

  return (
    <>
      <TableTitleBox title="Perfil | Mis notificaciones" />
      <Stack spacing={2} sx={{ bgcolor: 'background.paper', p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Tus notificaciones internas del sistema.
          </Typography>
          <CustomButton
            variant="base"
            onClick={() => markAllReadMutation.mutate({})}
            disabled={!data?.metrics?.unread || actionLoading}
          >
            Marcar todas leídas
          </CustomButton>
        </Stack>

        <Paper elevation={0}>
          <GeneralTableBox>
            <Table>
              <TableHead>
                <TableRow>
                  <HeadTableCell>Estado</HeadTableCell>
                  <HeadTableCell>Notificación</HeadTableCell>
                  <HeadTableCell>Contrato</HeadTableCell>
                  <HeadTableCell>Fecha</HeadTableCell>
                  <HeadTableCell>Acciones</HeadTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(isLoading || isFetching) && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <LoadingSkeleton />
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && !isFetching && notifications.length === 0 && (
                  <NoDataSkeleton colSpan={5} message="No tenés notificaciones." />
                )}
                {!isLoading &&
                  !isFetching &&
                  notifications.map((notification) => (
                    <TableRow key={notification.uuid} hover>
                      <RowTableCell>{readStatusChip(notification)}</RowTableCell>
                      <RowTableCell sx={{ minWidth: 320 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: notification.is_read ? 600 : 800 }}>
                          {notification.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {notification.message}
                        </Typography>
                      </RowTableCell>
                      <RowTableCell>{notification.contract_number || '-'}</RowTableCell>
                      <RowTableCell>
                        {formatDateTime(notification.generated_at || notification.created_at)}
                      </RowTableCell>
                      <RowTableCell>
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Ver">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDetail(notification)}
                              disabled={actionLoading}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={notification.is_read ? 'Marcar no leída' : 'Marcar leída'}>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleRead(notification)}
                              disabled={actionLoading}
                            >
                              {notification.is_read ? (
                                <MarkEmailUnread fontSize="small" />
                              ) : (
                                <DoneAll fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </RowTableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </GeneralTableBox>
        </Paper>
        <PaginationTable
          totalPages={data?.total_pages || 1}
          totalCount={data?.total || 0}
          page={page}
          onPageChange={(_, value) => setPage(value)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(1);
          }}
        />
      </Stack>

      <DialogContainer open={Boolean(detailNotification)} onClose={handleCloseDetail} size="small" minHeight="auto">
        <DialogTitle>Detalle de notificación</DialogTitle>
        <DialogBody sx={{ display: 'block' }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {detailNotification?.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {detailNotification?.message}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {readStatusChip(detailNotification)}
              {detailNotification?.contract_number && (
                <Chip size="small" label={`Contrato ${detailNotification.contract_number}`} />
              )}
              {detailNotification?.is_urgent && <Chip size="small" label="Urgente" color="error" />}
            </Stack>
            <Typography variant="caption" color="text.disabled">
              {formatDateTime(detailNotification?.generated_at || detailNotification?.created_at)}
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <CustomButton variant="warning" onClick={handleCloseDetail}>
                Cerrar
              </CustomButton>
            </Stack>
          </Stack>
        </DialogBody>
      </DialogContainer>
    </>
  );
};

export default ProfileNotifications;
